/**
 * `/me` — everything this server records about the person reading the page, and nothing else.
 *
 * IT READS WITH THE PHONE'S PLAYER SESSION, NOT WITH THE SIGN-IN (`SessionProvider`'s `self`). For a
 * player those are one token; for a member of staff who also plays they are two — the staff sign-in
 * names no player, and the player session is re-minted from this device's renewal, which an `/audio`
 * claim or a linked Discord sign-in left behind. So staff see their own stats like anybody else, and
 * nobody is signed out of this page by closing the app (owner feedback, 2026-10-12).
 *
 * THE URL CARRIES NO IDENTIFIER, AND THAT IS THE FEATURE. There is no `/me/:uuid` and no
 * `?player=`: the account this page is about is the `uuid` claim on the signed token, which the
 * sidecar copies onto its one-minute PostgREST token and every row-level-security policy in
 * web-roles.sql section 5 compares to `player_uuid`. So there is nothing in this URL to increment,
 * paste or share, and a player who does put somebody else's UUID somewhere gets their own rows
 * anyway — the parameter of the filter is not in their hands.
 *
 * THE REQUESTS CARRY NO IDENTIFIER EITHER. The staff page filters `player_uuid=eq.<uuid>` because
 * it is choosing which player to look at; this page sends NO filter at all, because the `web_self`
 * views are already narrowed to the caller. Adding one would be harmless and would be a lie about
 * where the narrowing happens — and the first reader who deleted it as redundant would be right
 * for the wrong reason.
 *
 * NOTHING HERE IS A WRITE, AND THE PAGE SAYS SO WHERE IT MATTERS. A player cannot claim a reward,
 * rename a home or change a teleport preference from the browser: the whole `web_player` role holds
 * SELECT and nothing else. The captions name the in-game command instead of offering a control that
 * would 403.
 *
 * IT NO LONGER LOOKS LIKE THE STAFF TOOL, AND THAT IS THE POINT OF THE `me/` DIRECTORY BESIDE IT.
 * The first version of this page reused the inspector's generic `ViewCard` for all seven views, and
 * so inherited the inspector's register: eight tables of raw columns, `yaw` and `pitch` and
 * surrogate ids, JSON payloads, ISO timestamps — a page built for an admin comparing rows, shown to
 * a player on a phone. What is shared with `/player/:uuid` now is DATA and not presentation: the row
 * types, and `../player/viewSpecs` for the copy in two voices. `me/me.css` is scoped under `.dc-me`
 * so it cannot reach a staff page, and nothing in `me/` is imported by one. The staff pages are
 * byte-for-byte as they were.
 *
 * THE READS ARE EXACTLY AS THEY WERE. Same eight `web_self` views, same player token, same absence
 * of any filter, same 30-second poll. This run changed how the rows are drawn and changed nothing
 * about which rows can be asked for — that limit lives in Postgres, not here.
 *
 * WHO THE HEADER NAMES COMES FROM THE CLAIM FIRST (plan §9.11, the Rotom Phone's Stats app). The
 * `/audio` claim already carries the player's name and skin hash — the Music app's player card is
 * drawn from them — so the header is too, with the directory row as the fallback for a session that
 * carries neither. It never falls back to the UUID's text: a player whose directory row had not loaded
 * used to be greeted as "90".
 *
 * A VIEW THAT IS NOT THERE IS NOT A FAILED READ. On a server whose game server has never run against
 * this Postgres, every `web_self` view answers 404 (`42P01 relation does not exist`): the polling hook
 * stops asking, and this page says so once, calmly, instead of seven cards promising to keep trying.
 */
import { Fragment, useState } from 'react';
import { Link } from 'react-router';

import { isStaff, type PlayerDirectoryRow, type PlayerViewName } from '@dimensionscore/shared';

import { usePolledView, type PolledView } from '../api/usePolledView';
import { useAudioCompanionState } from '../audio/AudioCompanionProvider';
import { PlayerHead } from '../audio/parts';
import { readPlayerHint, type AudioPlayerHint } from '../audio/prefs';
import { useSession } from '../auth/SessionProvider';
import { SignInPanel } from '../components/SessionBar';
import { friendlyDate, friendlyInstant, isoOf } from '../me/format';
import { BasesCard, HomesCard, LogoutCard, MailboxCard, RedemptionsCard, TeleportCard, TrustsCard } from '../me/sections';
import { SELF_VIEW_SPECS, type SelfViewSpec } from '../player/viewSpecs';

import '../me/me.css';

/**
 * Slower than the inspector's ten seconds, deliberately.
 *
 * An admin watching a grant land needs the next tick; a player looking at their own homes does not,
 * and there may be hundreds of them at once where there are a handful of admins. Eight views on
 * this interval is sixteen requests a minute per open tab, which is what the Worker's player budget
 * (`DEFAULT_PLAYER_RATE_LIMIT_PER_MINUTE`, 60) is sized from. Refetch-on-focus still applies, so
 * the page a player alt-tabs back to is fresh on the tick they look at it.
 */
export const PROFILE_POLL_INTERVAL_MS = 30_000;

/** The seven card views, plus the directory row this page's header is built from. */
type SelfViewName = Exclude<PlayerViewName, 'player_directory'>;

/** Who the page belongs to, as far as the tab knows: never the UUID's text in place of a name. */
export interface PageOwner {
  readonly uuid: string | null;
  readonly name: string | null;
  readonly skin: string | null;
}

/**
 * The header's player, from the best source the tab has.
 *
 * 1. What the claim answered — the companion's identity, or the hint it left on this device — for the
 *    session's own uuid: the name the mod sent and the skin hash beside it.
 * 2. A session minted BY a claim (`sub` is `minecraft:<uuid>`) carries that same name.
 * 3. The directory row's `last_known_name`.
 *
 * A Discord session's `name` is the Discord username, not who the reader plays as, so it is only ever
 * the heading's last resort (the caller's), and nothing here derives a name from the UUID.
 */
export function pageOwner(
  claims: { readonly sub: string; readonly name: string; readonly uuid?: string },
  listening: AudioPlayerHint | null,
  hint: AudioPlayerHint | null,
  row: PlayerDirectoryRow | null,
): PageOwner {
  const uuid = claims.uuid ?? listening?.uuid ?? row?.player_uuid ?? null;
  const ours = (candidate: AudioPlayerHint | null): candidate is AudioPlayerHint =>
    candidate !== null && uuid !== null && candidate.uuid.toLowerCase() === uuid.toLowerCase();
  const heard = ours(listening) ? listening : ours(hint) ? hint : null;
  const claimed = claims.sub.startsWith('minecraft:') && claims.name.trim() !== '' ? claims.name : null;
  const directory = row?.last_known_name ?? null;
  return { uuid, name: heard?.name ?? claimed ?? (directory === null || directory.trim() === '' ? null : directory), skin: heard?.skin ?? null };
}

export function MyProfilePage(): React.JSX.Element {
  const { session, self, selfChecking, selfUnavailable, selfClient: client, refreshSelf } = useSession();
  const companion = useAudioCompanionState();
  // Read once per visit: the companion refreshes it, and the page remounts whenever the app changes.
  const [hint] = useState(() => readPlayerHint());
  /*
   * ONLY A PLAYER SESSION READS. Without one there is nobody's rows to ask for: an `unlinked` sign-in
   * (in the guild, bound to no Minecraft account) is refused by the sidecar with 403 `no_read_plane`,
   * and a staff sign-in reads the staff plane, which is the inspector's. Firing eight requests to prove
   * either would fill the console, spend the edge budget and tell the reader nothing they can act on —
   * the instructions below are the whole page for them.
   */
  const enabled = self !== null;
  const options = { enabled, intervalMs: PROFILE_POLL_INTERVAL_MS };

  const profile = usePolledView(client, 'player_directory', {}, options);

  /*
   * One hook per view, written out rather than looped: the Rules of Hooks require a fixed call
   * order. `satisfies` keeps the per-view row types precise AND keeps the set TOTAL over
   * SelfViewName, so a ninth `web_self` view added to PLAYER_VIEW_NAMES stops this file compiling
   * until it is wired up here — the same guarantee `/player/:uuid` has, and the reason the two
   * pages cannot drift apart on which views exist.
   */
  const states = {
    player_homes: usePolledView(client, 'player_homes', {}, options),
    player_pending_rewards: usePolledView(client, 'player_pending_rewards', {}, options),
    player_redemptions: usePolledView(client, 'player_redemptions', {}, options),
    player_secret_bases: usePolledView(client, 'player_secret_bases', {}, options),
    player_base_trusts: usePolledView(client, 'player_base_trusts', {}, options),
    player_logout_placement: usePolledView(client, 'player_logout_placement', {}, options),
    player_teleport_prefs: usePolledView(client, 'player_teleport_prefs', {}, options),
  } satisfies Record<SelfViewName, PolledView<object>>;

  if (self === null) {
    // Still asking this device's renewal who it plays as: say nothing yet rather than the wrong thing.
    if (selfChecking) return <FindingYou />;
    if (session === null) return <SignInPanel />;
    if (session.claims.role === 'unlinked') return <NotLinkedYet name={session.claims.name} />;
    /*
     * A SIGN-IN THAT NAMES NO PLAYER YET — in practice a member of staff whose Discord account nobody has
     * linked, on a device that never claimed an `/audio` link. Said in a player's words: what is missing
     * is which Minecraft account is theirs, and both ways to say so are one command away.
     */
    return <WhichPlayerAreYou unavailable={selfUnavailable} onCheckAgain={refreshSelf} staff={isStaff(session.claims.role)} />;
  }

  const row = (profile.rows?.[0] ?? null) as PlayerDirectoryRow | null;
  // Ready with no row is a real state and not an error: the token names a player, and a profile
  // row is what the mod writes on join. It cannot happen for a linked account (the binding lives on
  // that very row), so it means the row was removed under the session.
  const noProfile = profile.status === 'ready' && row === null;
  const owner = pageOwner(self.claims, companion.state.phase === 'ready' ? companion.state.identity : null, hint, row);
  const name = owner.name ?? self.claims.name;
  // The face's only inputs, and neither reaches the DOM as text: a skin hash, or the uuid the
  // generated face is drawn from as pixels (audio/head.ts). The page prints no UUID anywhere.
  const uuid = owner.uuid;

  /*
   * NOT SWITCHED ON: every view answered 404, so this server's Postgres has never seen the game
   * server's migrations. One notice instead of seven failures, and nothing more is asked (the hook
   * stopped). A view that is missing while others answer is the same fact about fewer views.
   */
  const absent = SELF_VIEW_SPECS.filter((spec) => states[spec.view].missing);
  const present = SELF_VIEW_SPECS.filter((spec) => !states[spec.view].missing);
  const nothingSwitchedOn = profile.missing && present.length === 0;

  /*
   * EVERY CARD, KEYED BY ITS VIEW, so the `Record` is total over SelfViewName exactly like the
   * states above: a new `web_self` view is a compile error here until somebody has decided how to
   * SAY it, which is the one thing a generic table could never ask for. The ORDER is
   * `SELF_VIEW_SPECS`' order, so the two pages still agree on it in one place.
   */
  const cards: Record<SelfViewName, (spec: SelfViewSpec) => React.JSX.Element> = {
    player_homes: (spec) => <HomesCard spec={spec} state={states.player_homes} />,
    player_pending_rewards: (spec) => <MailboxCard spec={spec} state={states.player_pending_rewards} />,
    player_redemptions: (spec) => <RedemptionsCard spec={spec} state={states.player_redemptions} />,
    player_secret_bases: (spec) => <BasesCard spec={spec} state={states.player_secret_bases} />,
    player_base_trusts: (spec) => <TrustsCard spec={spec} state={states.player_base_trusts} />,
    player_logout_placement: (spec) => <LogoutCard spec={spec} state={states.player_logout_placement} selfUuid={uuid} />,
    player_teleport_prefs: (spec) => <TeleportCard spec={spec} state={states.player_teleport_prefs} />,
  };

  /*
   * "NOTHING YET" IS THE COMMON CASE, NOT THE EDGE, so it gets its own welcome rather than seven
   * silent empty cards. `settled` is what makes it honest: it is only claimed once every view that
   * exists has actually answered, so a slow connection never gets told its account is empty.
   */
  const views = present.map((spec) => states[spec.view] as PolledView<object>);
  const settled = views.length > 0 && views.every((view) => view.status === 'ready');
  const brandNew = settled && views.every((view) => (view.rows ?? []).length === 0);

  const hero = (
    <section className="me-hero" aria-labelledby="me-name">
      {uuid !== null && (
        <span className="me-avatar me-avatar-head" data-testid="me-avatar" data-face={owner.skin === null ? 'generated' : 'skin'}>
          <PlayerHead uuid={uuid} skin={owner.skin} size={56} />
        </span>
      )}
      <div className="me-hero-text">
        <p className="me-eyebrow">Your account</p>
        <h1 id="me-name" data-testid="me-name">
          {name}
        </h1>
        {row !== null && (
          <p className="me-hero-facts">
            <LastSeen value={row.last_seen} />
            <Since value={row.created_at} />
          </p>
        )}
      </div>
      <ul className="me-badges">
        {row?.discord_id !== null && row?.discord_id !== undefined && (
          <li className="me-badge" data-tone="good" data-testid="me-discord-badge">
            <span className="me-dot" />
            Discord linked
          </li>
        )}
      </ul>
    </section>
  );

  if (nothingSwitchedOn) {
    return (
      <div className="dc-me" data-testid="page-me">
        {hero}
        <section className="me-panel" role="status" aria-labelledby="me-off-title" data-testid="me-not-switched-on">
          <h2 id="me-off-title">Your stats aren&rsquo;t switched on for this server yet</h2>
          <p>
            This page shows what the server keeps for you — homes, rewards waiting, gift codes, bases — once the game server has set its database up
            for it. That hasn&rsquo;t happened on this server yet. Nothing of yours is missing: it is all still in the game, and your music and the map
            work as they are.
          </p>
          <p className="me-note">Open Stats again later; it fills in once the server is set up.</p>
        </section>
      </div>
    );
  }

  return (
    <div className="dc-me" data-testid="page-me">
      {hero}

      {absent.length > 0 && (
        <section className="me-panel" role="status" data-testid="me-partly-switched-on">
          <h2>Some of your stats aren&rsquo;t switched on for this server yet</h2>
          <p>
            {absent.map((spec) => spec.title).join(', ')} {absent.length === 1 ? 'is' : 'are'} not set up on this server&rsquo;s database yet, so{' '}
            {absent.length === 1 ? 'it is' : 'they are'} left off this page. Everything else below is current.
          </p>
        </section>
      )}

      {noProfile && (
        <section className="me-panel" role="status" data-testid="me-no-profile">
          <h2>We cannot find your profile</h2>
          <p>
            Your sign-in names a Minecraft account this server has no profile for. That should not be possible for a linked account, so it is worth
            telling whoever runs the server — nothing you did caused it, and nothing of yours is lost.
          </p>
        </section>
      )}

      {/*
        THE ONE THING THIS PAGE ASKS FOR, and only when the database says it is missing.
        `discord_id` is null on a player's own directory row exactly when there is no binding
        (migration 1.10, and `PlayerDirectoryRow.discord_id`'s own note). For a `player` token that
        is a contradiction resolved in one direction: the token was minted FROM a binding, so a null
        here means it was removed while this session was open. Either way `/link` is the remedy, and
        it is one click away rather than an instruction to go and find it.
      */}
      {row !== null && row.discord_id === null && (
        <section className="me-panel" data-tone="accent" aria-labelledby="me-link-nudge-title" data-testid="me-link-nudge">
          <h2 id="me-link-nudge-title">Link your Discord to this account</h2>
          <p>
            This page can show you your things, but the Discord side of the link is missing — so nothing on Discord knows this is you. Run{' '}
            <code>/link</code> in game for a code, then enter it here.
          </p>
          <Link className="me-cta" to="/link" data-testid="me-link-nudge-cta">
            Enter a link code →
          </Link>
        </section>
      )}

      {brandNew ? (
        <section className="me-panel" data-tone="accent" aria-labelledby="me-welcome-title" data-testid="me-welcome">
          <h2 id="me-welcome-title">Welcome, {name} — your account is ready</h2>
          <p>
            There is nothing stored for you yet, which is exactly what a new account looks like. Three things in game and this page starts filling
            up:
          </p>
          <ol className="me-steps">
            <li>
              Stand somewhere you like and run <code>/sethome home</code>.
            </li>
            <li>
              Start your own private world with <code>/sb create</code>.
            </li>
            <li>
              Collect anything the server is holding for you with <code>/rewards claimall</code>.
            </li>
          </ol>
        </section>
      ) : (
        <ul className="me-tiles" aria-label="A summary of what is on this page">
          {!states.player_homes.missing && <SummaryTile view="player_homes" label="Homes" state={states.player_homes} />}
          {!states.player_pending_rewards.missing && (
            <SummaryTile view="player_pending_rewards" label="Waiting for you" state={states.player_pending_rewards} />
          )}
          {!states.player_secret_bases.missing && <SummaryTile view="player_secret_bases" label="Bases" state={states.player_secret_bases} />}
          {!states.player_redemptions.missing && <SummaryTile view="player_redemptions" label="Gift codes" state={states.player_redemptions} />}
        </ul>
      )}

      {present.map((spec) => (
        <Fragment key={spec.view}>{cards[spec.view](spec)}</Fragment>
      ))}

      {/*
        WHAT THIS PAGE CANNOT SHOW, said out loud rather than left as an absence. A page that lists
        "everything about you" implicitly claims to be complete, and this one is complete only about
        what the database holds: playtime, currency balances and Cobblemon progress are not rows in
        this schema at all (balances live in MultiEconomy, party and PC in Cobblemon's own storage),
        so there is nothing here to read and no amount of front-end work would find it.
      */}
      <section className="me-card me-scope" aria-labelledby="me-scope-title">
        <h2 id="me-scope-title">What is not on this page</h2>
        <p data-testid="me-scope-note">
          Playtime, your balance and your Cobblemon are kept by other systems, so this page cannot see them. What it does show is everything this
          server stores about your account: homes, your reward mailbox, redeemed gift codes, bases and teleport settings. All of it is read-only —
          the in-game commands are still the way to change any of it.
        </p>
      </section>
    </div>
  );
}

/**
 * One summary tile, which is a jump link and not a statistic.
 *
 * A number on its own would be a dashboard, and a player has no use for a dashboard of four
 * numbers; what they want on a phone is to reach the section without scrolling past three others.
 * So each tile is an in-page anchor to the card it counts, keyboard reachable like any link, and it
 * says `—` rather than `0` until its view has actually answered.
 */
function SummaryTile({ view, label, state }: { view: SelfViewName; label: string; state: PolledView<object> }): React.JSX.Element {
  const count = state.rows === null ? null : state.rows.length;
  return (
    <li>
      <a className="me-tile" href={`#view-${view}`} data-testid={`me-tile-${view}`}>
        <span className="me-tile-value">{count ?? '—'}</span>
        <span className="me-tile-label">{label}</span>
      </a>
    </li>
  );
}

/** "last seen today at 12:41", or "not seen in game yet" for a profile nobody has joined with. */
function LastSeen({ value }: { value?: string | null }): React.JSX.Element {
  const said = friendlyInstant(value);
  const iso = isoOf(value);
  if (said === null || iso === null) return <span>Not seen in game yet</span>;
  return (
    <span>
      Last seen <time dateTime={iso}>{said}</time>
    </span>
  );
}

/** "playing here since 2 Jan 2026", from the profile row the mod writes on first join. */
function Since({ value }: { value?: string | null }): React.JSX.Element | null {
  const said = friendlyDate(value);
  const iso = isoOf(value);
  if (said === null || iso === null) return null;
  return (
    <span>
      Here since <time dateTime={iso}>{said}</time>
    </span>
  );
}

/** While the provider asks this device's renewal who it plays as: a moment, said quietly. */
function FindingYou(): React.JSX.Element {
  return (
    <div className="dc-me">
      <section className="me-hero" aria-busy="true" data-testid="page-me-finding">
        <div className="me-hero-text">
          <p className="me-lede">Finding your stats…</p>
        </div>
      </section>
    </div>
  );
}

/**
 * A sign-in that names no Minecraft player yet, in a player's words.
 *
 * It replaced a staff-only refusal that explained token scopes and "the unfiltered plane" to the reader
 * (owner feedback: "this is stupid text to be player facing, and it should work somehow"). It does work
 * now — a linked Discord account or an `/audio` claim on this device is all this page needs — so what is
 * left to say is how to give it one of those, not why it cannot.
 */
function WhichPlayerAreYou({
  unavailable,
  onCheckAgain,
  staff,
}: {
  readonly unavailable: boolean;
  readonly onCheckAgain: () => void;
  readonly staff: boolean;
}): React.JSX.Element {
  return (
    <div className="dc-me">
      <section className="me-hero" aria-labelledby="me-which-title" data-testid="page-me-which-player">
        <div className="me-hero-text">
          <p className="me-eyebrow">Almost there</p>
          <h1 id="me-which-title">Which player are you?</h1>
          <p className="me-lede">Your stats belong to your Minecraft account, and this phone does not know which one is yours yet.</p>
          {unavailable && (
            <p className="me-lede" data-testid="me-which-unavailable">
              We could not check just now whether your Discord is linked. Try again in a moment.
            </p>
          )}
        </div>
      </section>

      <section className="me-panel" data-tone="accent" aria-labelledby="me-which-steps-title">
        <h2 id="me-which-steps-title">Two ways to show it</h2>
        <ol className="me-steps">
          <li>
            In game, type <code>/audio</code> and open the link on this device. Your phone remembers you after that.
          </li>
          <li>
            Or type <code>/link</code> in game and enter the code here. That connects your Discord account to your Minecraft account for good.
          </li>
        </ol>
        <p className="me-steps-actions">
          <Link className="me-cta" to="/link" data-testid="me-which-link-cta">
            Enter a link code →
          </Link>{' '}
          <button type="button" className="me-cta me-cta-quiet" onClick={onCheckAgain} data-testid="me-which-check-again">
            Check again
          </button>
        </p>
        {staff && (
          <p className="me-caption">
            The staff tools are still where they were: <Link to="/">open the console</Link>.
          </p>
        )}
      </section>
    </div>
  );
}

/**
 * What an `unlinked` session sees: the instruction, and the page that finishes it.
 *
 * THIS IS THE WHOLE POINT OF THAT TIER. Before it existed, a Discord account with no staff role and
 * no binding was refused a token altogether, and the refusal advised running `/link` — an
 * instruction that could not be carried out, because entering the code needs a session. So this is
 * not a nicer 403: it is the state in which the remedy is actually available, one link away.
 *
 * IT IS DRESSED AS A WELCOME, NOT AS A REFUSAL. The reader is a player who has just signed in
 * successfully and is two commands from a working account; a warning-coloured "not linked" box
 * would tell them something went wrong, and nothing has. It wears the player palette (`.dc-me`) for
 * the same reason: this is the first page of theirs they will ever see.
 */
function NotLinkedYet({ name }: { name: string }): React.JSX.Element {
  return (
    <div className="dc-me">
      <section className="me-hero" aria-labelledby="me-unlinked-title" data-testid="page-me-unlinked">
        <div className="me-hero-text">
          <p className="me-eyebrow">One step to go</p>
          <h1 id="me-unlinked-title">Link your Minecraft account</h1>
          <p className="me-lede" data-testid="me-link-instructions">
            You are signed in as <strong>{name}</strong>, and this Discord account is not linked to a Minecraft account yet — so there is nothing of
            yours to show you.
          </p>
        </div>
      </section>

      <section className="me-panel" data-tone="accent" aria-labelledby="me-link-steps-title">
        <h2 id="me-link-steps-title">How to link, in three steps</h2>
        <ol className="me-steps">
          <li>
            Join the server and type <code>/link</code> in chat.
          </li>
          <li>It answers with a short code, good for a few minutes.</li>
          <li>Enter that code on the link page, and this page becomes yours.</li>
        </ol>
        <Link className="me-cta" to="/link" data-testid="me-link-cta">
          Enter a link code →
        </Link>
      </section>
    </div>
  );
}
